/**
 * StatusTable function displays a table of taxon statuses using the DataGrid function from MUI.
 * It fetches the status list from the StoreStatus hook and generates unique columns and rows based on the status data.
 * The table includes a button to export the data as a CSV file.
 *
 * @param {Object} props - The function props.
 * @param {Array} props.taxonList - The list of taxons to be displayed in the table.
 *
 * @returns {JSX.Element} The rendered StatusTable function.
 */

import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { 
  DataGrid, 
  GridRowsProp, 
  GridColDef,
  useGridApiRef
} from '@mui/x-data-grid';
import { StoreStatus } from '../../hooks/store';
import { useTranslation } from 'react-i18next';

export default function StatusTable({ taxonList } : any) {

  const { t } = useTranslation();

  const { response: statusList } = StoreStatus();

  function generateUniqueColumns(statusList:any) {
    const columns: any = {};
    statusList.map((status:any) => {
      if (!status.terrUnit || !status.statusType) return;
      const columnKey = `${status.terrUnit.terrUnitName} : ${status.statusType.shortened}`;
      columns[columnKey] = {
        terrUnitName: status.terrUnit.terrUnitName,
        terrUnitKey: status.terrUnit.terrUnitKey,
        statusType: status.statusType.shortened
      };
    });

    const columnArray = Object.values(columns);

    return {
      columns: columnArray
    };
  }

  const headers = (generateUniqueColumns(statusList).columns);

  const stringifyTerrUnitColumn = (headers as { terrUnitName: any, statusType: any }[]).map((column) => {
    return toColumnLabel(column);
  }).sort();

  function toColumnLabel(column: any) {
    return column.terrUnitName + ' : ' + column.statusType;
  }

const generateUniqueRows = (statusList: any[], columnsTerrUnit: any[]): GridRowsProp => {
  // Construire une map txKey → statuts
  const taxonMap = new Map();

  statusList.forEach((taxonStatutes) => {
    const txKey = taxonStatutes.taxa.txKey;
    if (!taxonMap.has(txKey)) {
      taxonMap.set(txKey, {
        id: txKey,
        taxon: taxonStatutes.taxa.txScientificName,
        ...Object.fromEntries(columnsTerrUnit.map(col => [toColumnLabel(col), []]))
      });
    }
    columnsTerrUnit.forEach(column => {
      if (
        column.terrUnitKey === taxonStatutes.terrUnit.terrUnitKey &&
        column.statusType === taxonStatutes.statusType.shortened
      ) {
        taxonMap.get(txKey)[toColumnLabel(column)].push(taxonStatutes.statusType.value);
      }
    });
  });

  // Construire une map validTaxon → statuts fusionnés
  const validTaxonMap = new Map();
  taxonList.forEach((taxon: any) => {
    const validName = taxon.validName || '';
    if (!validName) return;
    // On cherche les statuts du taxon valide
    const validRow = Array.from(taxonMap.values()).find(row => row.taxon === validName);
    if (!validRow) return;
    if (!validTaxonMap.has(validName)) {
      validTaxonMap.set(validName, {});
    }
    columnsTerrUnit.forEach(col => {
      const colLabel = toColumnLabel(col);
      const current = validTaxonMap.get(validName)[colLabel] || [];
      const toAdd = validRow[colLabel] || [];
      validTaxonMap.get(validName)[colLabel] = Array.from(new Set([...current, ...toAdd]));
    });
  });

  // Générer les lignes à partir de taxonList
  const rowsArray = taxonList.map((taxon: any) => {
    const txKey = taxon.id;
    const validName = taxon.validName || '';
    const row = taxonMap.get(txKey) || {
      id: txKey,
      taxon: taxon.name,
      ...Object.fromEntries(columnsTerrUnit.map(col => [toColumnLabel(col), []]))
    };

    // Fusionner les statuts du taxon valide si besoin
    let mergedCols = {};
    if (validName && validTaxonMap.has(validName)) {
      mergedCols = Object.fromEntries(
        columnsTerrUnit.map(col => {
          const colLabel = toColumnLabel(col);
          const values = Array.from(new Set([
            ...(row[colLabel] || []),
            ...(validTaxonMap.get(validName)[colLabel] || [])
          ]));
          return [colLabel, values.join(', ')];
        })
      );
    } else {
      mergedCols = Object.fromEntries(
        columnsTerrUnit.map(col => {
          const colLabel = toColumnLabel(col);
          return [colLabel, (row[colLabel] || []).join(', ')];
        })
      );
    }

    return {
      id: txKey,
      taxon: taxon.name,
      validTaxon: validName,
      ...mergedCols
    };
  });

  return rowsArray;
};

  var rows: GridRowsProp = generateUniqueRows(statusList, headers);

  var columns: GridColDef[] = [
    {
      field: 'taxon',
      headerName: 'Taxon Cités',
      renderHeader: () => (
        <div style={{ 
          fontWeight: 'bold'
        }}>
          {t("taxa-name-statutes")}
        </div>
      ),
      width: 200,
      align: 'left',
      headerAlign: 'left'
    },
    {
      field: 'validTaxon',
      headerName: 'Taxon Valides',
      renderHeader: () => (
        <div style={{ 
          fontWeight: 'bold'
        }}>
          {t("taxa-valid-statutes")}
        </div>
      ),
      width: 200,
      align: 'left',
      headerAlign: 'left'
    },
    ...stringifyTerrUnitColumn.map((item: any) => (
      {
        field: item, 
        headerName: item, 
        renderHeader: () => (
          <div style={{ 
            fontWeight: 'bold'
          }}>
            {item}
          </div>
        ),
        width: 200
      }))
  ];

  const apiRef = useGridApiRef();

  return (
    <Box
      sx={{
        height: 380,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <DataGrid
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        apiRef={apiRef}
      />
      <Box
        sx={{
          display:"flex",
          justifyContent:" center",
          m: 2
        }}
      >
      <Button
        startIcon={<FileDownloadIcon />}
        color='primary'
        onClick={() => apiRef.current.exportDataAsCsv(
          {
            allColumns: true,
            fileName: 'Linnae_status_table',
            delimiter: ';',
          }
        )}
        variant='outlined'
      >
        {t("export-button")}
      </Button>
    </Box>
  </Box>
  );
}